import { Controller } from "stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
  static targets = [
    "durationHours", 
    "durationMinutes", 
    "durationTotal", 
    "unassignedContainer", 
    "unassignedText"
  ];

  static values = {
    sessionDurationMinutes: Number,
    enrolledSessionIds: Array
  };

  connect() {
    this.getInitialAttendanceValues();
  }

  getInitialAttendanceValues() {
    if (document._initialAttendanceValues) return;

    const snapshot = {};
    document.querySelectorAll("input[id^='attendance_duration_']").forEach(el => {
      const id = el.id.replace("attendance_duration_", "");
      snapshot[id] = {
        duration: el.value,
        notes: (document.getElementById(`attendance_notes_input_${id}`) || {}).value || "",
        move: (document.getElementById(`attendance_move_${id}`) || {}).value || ""
      };
    });
    document._initialAttendanceValues = snapshot;
  }

  formHasChanges() {
    if (!document._initialAttendanceValues) return false;

    const snapshot = document._initialAttendanceValues;

    for (const id of Object.keys(snapshot)) {
      const duration = (document.getElementById(`attendance_duration_${id}`) || {}).value || "";
      const notes = (document.getElementById(`attendance_notes_input_${id}`) || {}).value || "";
      const move = (document.getElementById(`attendance_move_${id}`) || {}).value || "";

      if (duration !== snapshot[id].duration ||
          notes !== snapshot[id].notes ||
          move !== snapshot[id].move) {
        return true;
      }
    }
    return false;
  }

  async showQuickLook(event) {
    event.preventDefault();

    const attendanceId = this.data.get("attendanceId");
    const durationInput = document.getElementById(`attendance_duration_${attendanceId}`);
    const durationMinutes = durationInput ? durationInput.value : null;
    const notesInput = document.getElementById(`attendance_notes_input_${attendanceId}`);
    const notesValue = notesInput ? notesInput.value : null;
    const moveInput = document.getElementById(`attendance_move_${attendanceId}`);
    const moveValue = moveInput ? moveInput.value : null;

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const params = new URLSearchParams();
    if (durationMinutes !== null) {
      params.append("duration_in_minutes", durationMinutes);
    }
    if (notesValue !== null) {
      params.append("notes", notesValue);
    }
    if (moveValue) {
      params.append("move_session_id", moveValue);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`/schools/classroom/sessions/attendance/${attendanceId}/quick-look${query}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (response.ok) {
      const quickViewElement =
        document.querySelector("#school-classroom-session-attendance-quick-look") ||
        document.querySelector("#school_classrooms_quick_look");
      
      if (!quickViewElement) {
        console.error("Quick view element not found in the DOM.");
        return;
      }

      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      if (Turbo && typeof Turbo.renderStreamMessage === "function") {
        Turbo.renderStreamMessage(responseBody);
      } else {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(responseBody, "text/html");
        const template = xmlDoc.querySelector("template");

        if (template) {
          quickViewElement.innerHTML = "";
          quickViewElement.appendChild(template.content.cloneNode(true));
        }
      }
    }
  }
  
  closeQuickLook(event) {
    if (event) event.preventDefault();

    const quickViewElement = document.querySelector("#school-classroom-session-attendance-quick-look");

    if (!quickViewElement) {
      console.error("Quick view element not found in the DOM.");
      return;
    }

    quickViewElement.classList.add("u-hidden");
  }

  updateDuration() {
    if (!this.hasDurationHoursTarget || !this.hasDurationMinutesTarget || !this.hasDurationTotalTarget) {
      return;
    }

    const hours = parseInt(this.durationHoursTarget.value || "0", 10);
    const minutes = parseInt(this.durationMinutesTarget.value || "0", 10);
    const totalMinutes = (hours * 60) + minutes;

    this.durationTotalTarget.value = totalMinutes;

    this.updateUnassignedDisplay(totalMinutes);
  }

  updateUnassignedDisplay(attendedMinutes) {
    const sessionMinutes = this.sessionDurationMinutesValue || 0;
    const unassigned = Math.max(sessionMinutes - attendedMinutes, 0);
    const display = this.formatDuration(unassigned);

    this.unassignedContainerTargets.forEach(el => {
      el.style.display = unassigned > 0 ? "" : "none";
    });
    this.unassignedTextTargets.forEach(el => {
      el.textContent = display;
    });
  }

  async validate(event) {
    event.preventDefault();

    const errors = this.validateForm();
    if (errors.length > 0) {
      this.showValidationBlock(errors.join("<br>"));
      return;
    }

    const form = event.target.closest("form");
    const formData = new FormData(form);
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const response = await fetch(form.action, {
      method: form.method,
      headers: {
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: formData
    });

    if (response.ok) {
      const responseBody = await response.text();
      if (Turbo && typeof Turbo.renderStreamMessage === "function") {
        Turbo.renderStreamMessage(responseBody);
      }
      setTimeout(() => this.toggleUnsavedChangesBanner(), 100);
    }
  }

  validateForm() {
    let validationErrors = [];

    if (!this.hasDurationTotalTarget) {
      validationErrors.push("Duration is required.");
      return validationErrors;
    }

    const totalMinutes = parseInt(this.durationTotalTarget.value || "", 10);
    if (Number.isNaN(totalMinutes) || totalMinutes < 0) {
      validationErrors.push("Duration must be a valid number of minutes.");
      return validationErrors;
    }

    const maxMinutes = parseInt(this.durationTotalTarget.dataset.maxMinutes || "", 10);
    if (!Number.isNaN(maxMinutes) && maxMinutes > 0 && totalMinutes > maxMinutes) {
      validationErrors.push("Duration cannot exceed the session length.");
    }

    const sessionAttendanceTypeError = this.sessionAttendanceTypeError();
    if (sessionAttendanceTypeError) {
      validationErrors.push(sessionAttendanceTypeError);
    }

    const alreadyEnrolledError = this.sessionAlreadyEnrolledError();
    if (alreadyEnrolledError) {
      validationErrors.push(alreadyEnrolledError);
    }

    return validationErrors;
  }

  showValidationBlock(message) {
    const validationBlock = document.getElementById("validation-block");
    const validationMessage = document.getElementById("validation-message");
  
    validationMessage.innerHTML = message;
    validationBlock.hidden = false;
    validationMessage.hidden = false;
  }

  hideValidationBlock() {
    const validationBlock = document.getElementById("validation-block");
    if (validationBlock) validationBlock.hidden = true;
  }

  validateSessionSelection() {
    const error = this.sessionAttendanceTypeError() || this.sessionAlreadyEnrolledError();
    const updateButton = document.getElementById("quick-look-update-button");

    if (error) {
      this.showValidationBlock(error);
      if (updateButton) updateButton.disabled = true;
    } else {
      this.hideValidationBlock();
      if (updateButton) updateButton.disabled = false;
    }
  }

  sessionAttendanceTypeError() {
    const sessionSelect = this.element.querySelector("[name^='attendee_moves']");
    if (!sessionSelect) return null;

    const selectedOption = sessionSelect.selectedOptions[0];
    if (!selectedOption || !selectedOption.value) return null;

    const reassignElement = this.element.querySelector("[data-driving-school-classroom-session-reassign-attendance-type-value]");
    if (!reassignElement) return null;

    const attendanceType = reassignElement.dataset.drivingSchoolClassroomSessionReassignAttendanceTypeValue;

    if (attendanceType === "in_person" && selectedOption.dataset.inPerson === "false") {
      return "Selected session is not available for in-person attendance.";
    }
    if (attendanceType !== "in_person" && selectedOption.dataset.remote === "false") {
      return "Selected session is not available for remote attendance.";
    }

    return null;
  }

  sessionAlreadyEnrolledError() {
    const sessionSelect = this.element.querySelector("[name^='attendee_moves']");
    if (!sessionSelect) return null;

    const selectedOption = sessionSelect.selectedOptions[0];
    if (!selectedOption || !selectedOption.value) return null;

    const selectedId = parseInt(selectedOption.value, 10);
    if (this.enrolledSessionIdsValue.includes(selectedId)) {
      return "Student is already enrolled in the selected session.";
    }

    return null;
  }

  toggleUnsavedChangesBanner() {
    const banner = document.getElementById("unsaved-changes-message");
    if (!banner) return;

    if (this.formHasChanges()) {
      banner.classList.remove("u-hidden");
    } else {
      banner.classList.add("u-hidden");
    }
  }

  toggleAttendance(event) {
    const checkbox = event.target;
    if (!checkbox) {
      return;
    }

    const attendanceId = checkbox.dataset.attendanceId;
    if (!attendanceId) {
      return;
    }

    this.setDurationForCheckbox(checkbox);
    this.toggleUnsavedChangesBanner();
  }

  setDurationForCheckbox(checkbox) {
    const attendanceId = checkbox.dataset.attendanceId;
    if (!attendanceId) {
      return;
    }

    const durationInput = document.getElementById(`attendance_duration_${attendanceId}`);
    if (!durationInput) {
      return;
    }

    const sessionMinutes = Number.isFinite(this.sessionDurationMinutesValue)
      ? this.sessionDurationMinutesValue
      : 0;

    durationInput.value = checkbox.checked ? sessionMinutes : 0;

    const displayElement = document.getElementById(`attendance_duration_display_${attendanceId}`);
    if (displayElement) {
      displayElement.textContent = this.formatDuration(durationInput.value);
    }

    const durationIcon = document.getElementById(`attendance_duration_icon_${attendanceId}`);
    if (durationIcon) {
      const isFullDuration = parseInt(durationInput.value, 10) === sessionMinutes;
      durationIcon.classList.toggle("u-hidden", isFullDuration);
    }
  }

  formatDuration(durationMinutes) {
    const total = parseInt(durationMinutes || "0", 10);
    if (Number.isNaN(total) || total === 0) {
      return "-";
    }

    const hours = Math.floor(total / 60);
    const minutes = total % 60;

    if (hours === 0) {
      return `${minutes}m`;
    }
    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  }
}
